<template>
  <v-container fluid>
    <div class="toolbar d-flex flex-row justify-space-between align-center elevation-3 mb-3">
      <span>
        Product Groups Display
      </span>
      <div
        class="d-flex flex-row align-center"
        style="gap: 0.625rem;">
        <v-btn
          v-if="isEdit"
          color="error"
          outlined
          rounded
          small
          :loading="loading.contentLoading"
          :disabled="loading.mainLoading"
          @click="onReset()">
          {{ $t('button.Reset') }}
        </v-btn>
        <CloneToCountries
          :brand="mapBrand"
          :countries="shippingCountries"
          :current-country="query.country"
          :source-dirty="isEdit"
          :clone-fn="cloneCollections"
          entity-title="Product Groups"
          subtitle="Copy the collections and clubs to other countries."
          warning-text="current collections and clubs at each destination with the source's — items not in the source are removed,
            and the change is visible to distributors immediately."
          success-lead="Collections and clubs"
          success-note="All destination countries were updated."
          :known-errors="cloneKnownErrors"
          :unknown-error-is-partial="true"
          :disabled="loading.mainLoading || !mapBrand || !shippingCountries.length"
          @cloned="onCloned" />
        <v-btn
          color="success"
          rounded
          small
          :loading="loading.contentLoading"
          :disabled="loading.mainLoading || !isEdit"
          @click="updateAllItems()">
          {{ $t('button.Save') }}
        </v-btn>
      </div>
    </div>

    <v-row class="px-4">
      <v-col
        cols="12"
        md="7">
        <div class="card-container elevation-3">
          <v-row>
            <v-col
              cols="12"
              class="card-title">
              Brand to 📝 "DO"
            </v-col>
            <v-col cols="12">
              <v-tabs
                v-model="query.brand"
                color="secondary">
                <v-tab
                  v-for="brand in brands"
                  :key="brand">
                  {{ brand }}
                </v-tab>
              </v-tabs>
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col
        cols="12"
        md="5">
        <div class="card-container elevation-3">
          <v-row>
            <v-col
              cols="12"
              class="card-title">
              Country to 🗺️ "DELIVER"
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                v-model="query.country"
                placeholder="Country"
                color="secondary"
                prepend-inner-icon="mdi-flag-variant-outline"
                :items="shippingCountries"
                item-text="country"
                item-value="code"
                hide-details
                :loading="loading.mainLoading" />
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col
        v-show="query.country"
        cols="12">
        <v-form
          ref="form"
          @submit.prevent="updateAllItems()">
          <v-expansion-panels
            v-model="panels"
            multiple>
            <v-expansion-panel
              class="elevation-3 custom-pannel"
              style="border-radius: 0.625rem;">
              <v-expansion-panel-header>
                <span class="card-title">
                  Collection to 🖥️ "SHOW"
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-data-table
                  :headers="headers"
                  :items="collections"
                  :items-per-page="-1"
                  :loading="loading.mainLoading || loading.contentLoading"
                  hide-default-footer>
                  <template #body>
                    <Draggable
                      :list="collections"
                      tag="tbody"
                      group="itemsDisplay"
                      handle=".drag-handle">
                      <tr
                        v-for="(item, index) in collections"
                        :key="`${item.id}-${index}`">
                        <td
                          v-for="header in headers"
                          :key="`${header.value}-${index}`">
                          <v-icon
                            v-if="header.value === 'handle'"
                            class="drag-handle"
                            style="cursor: grab">
                            mdi-drag
                          </v-icon>
                          <v-text-field
                            v-if="header.value === 'name'"
                            v-model.trim="collections[index].name"
                            :rules="nameRules"
                            placeholder="คอเลกชั่น"
                            color="secondary"
                            dense
                            hide-details />
                          <v-text-field
                            v-else-if="header.value === 'path'"
                            v-model="collections[index].path"
                            :rules="pathRules"
                            placeholder="/collection/example-collection"
                            color="secondary"
                            dense
                            hide-details />
                          <div
                            v-else-if="header.value === 'tags'"
                            class="search-collection-tag">
                            <v-autocomplete
                              v-model="collections[index].tags"
                              :rules="requiredRules"
                              placeholder="แท็ก"
                              color="secondary"
                              :items="tags"
                              chips
                              small-chips
                              deletable-chips
                              multiple
                              dense
                              hide-details>
                              <template #prepend-inner>
                                <v-icon
                                  style="margin-top: 6px;"
                                  small>
                                  mdi-tag
                                </v-icon>
                              </template>
                            </v-autocomplete>
                          </div>
                          <div
                            v-else-if="header.value === 'action'"
                            class="d-flex flex-row justify-center align-center">
                            <v-btn
                              color="error"
                              small
                              icon
                              @click="removeItem('collection', index)">
                              <v-icon small>
                                mdi-delete
                              </v-icon>
                            </v-btn>
                          </div>
                          <div v-else>
                            {{ item[header.value] }}
                          </div>
                        </td>
                      </tr>
                    </Draggable>
                  </template>
                  <template #no-data>
                    <div class="no-data">
                      No data
                    </div>
                  </template>
                </v-data-table>
                <div class="d-flex justify-center align-center mt-6">
                  <v-btn
                    color="secondary"
                    rounded
                    small
                    :loading="loading.contentLoading"
                    :disabled="loading.mainLoading || query.country === null || query.brand === null"
                    @click="onAddItem('collection')">
                    <v-icon
                      class="mr-2"
                      small>
                      mdi-plus
                    </v-icon>
                    {{ $t('button.Add') }}
                  </v-btn>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel
              class="elevation-3 mt-4 custom-pannel"
              style="border-radius: 0.625rem;">
              <v-expansion-panel-header>
                <span class="card-title">
                  Club to 🖥️ "SHOW"
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-data-table
                  :headers="headers"
                  :items="clubs"
                  :items-per-page="-1"
                  :loading="loading.mainLoading || loading.contentLoading"
                  hide-default-footer>
                  <template #body>
                    <Draggable
                      :list="clubs"
                      tag="tbody"
                      group="itemsDisplay"
                      handle=".drag-handle">
                      <tr
                        v-for="(item, index) in clubs"
                        :key="`${item.id}-${index}`">
                        <td
                          v-for="header in headers"
                          :key="`${header.value}-${index}`">
                          <v-icon
                            v-if="header.value === 'handle'"
                            class="drag-handle"
                            style="cursor: grab">
                            mdi-drag
                          </v-icon>
                          <v-text-field
                            v-if="header.value === 'name'"
                            v-model.trim="clubs[index].name"
                            :rules="nameRules"
                            placeholder="คอเลกชั่น"
                            color="secondary"
                            dense
                            hide-details />
                          <v-text-field
                            v-else-if="header.value === 'path'"
                            v-model="clubs[index].path"
                            :rules="pathRules"
                            placeholder="/collection/example-collection"
                            color="secondary"
                            dense
                            hide-details />
                          <div
                            v-else-if="header.value === 'tags'"
                            class="search-collection-tag">
                            <v-autocomplete
                              v-model="clubs[index].tags"
                              :rules="requiredRules"
                              placeholder="แท็ก"
                              color="secondary"
                              :items="tags"
                              chips
                              small-chips
                              deletable-chips
                              multiple
                              dense
                              hide-details>
                              <template #prepend-inner>
                                <v-icon
                                  style="margin-top: 6px;"
                                  small>
                                  mdi-tag
                                </v-icon>
                              </template>
                            </v-autocomplete>
                          </div>
                          <div
                            v-else-if="header.value === 'action'"
                            class="d-flex flex-row justify-center align-center">
                            <v-btn
                              color="error"
                              small
                              icon
                              @click="removeItem('club', index)">
                              <v-icon small>
                                mdi-delete
                              </v-icon>
                            </v-btn>
                          </div>
                          <div v-else>
                            {{ item[header.value] }}
                          </div>
                        </td>
                      </tr>
                    </Draggable>
                  </template>
                  <template #no-data>
                    <div class="no-data">
                      No data
                    </div>
                  </template>
                </v-data-table>
                <div class="d-flex justify-center align-center mt-6">
                  <v-btn
                    color="secondary"
                    rounded
                    small
                    :loading="loading.contentLoading"
                    :disabled="loading.mainLoading || query.country === null || query.brand === null"
                    @click="onAddItem('club')">
                    <v-icon
                      class="mr-2"
                      small>
                      mdi-plus
                    </v-icon>
                    {{ $t('button.Add') }}
                  </v-btn>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-form>
      </v-col>
      <v-col
        v-if="!query.country"
        cols="12">
        <div class="card-container elevation-3">
          <v-row>
            <v-col cols="12">
              <div class="warning-box">
                <span>
                  Please select
                </span>
                <span class="font-weight-bold">
                  "COUNTRY"
                </span>
                <span>
                  to show contents
                </span>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Draggable from 'vuedraggable'
import { mapActions } from 'vuex'
import ProductAttributeProvider from '@/resources/ProductAttributeProvider'
import ExportCurrencyRatesProvider from '@/resources/ExportCurrencyRatesProvider'
import ExportCollectionsProvider from '@/resources/ExportCollectionsProvider'
import { getRole } from '@/assets/js/Authentication'
import ShippingCountries from '@/assets/json/ShippingCountries.json'
import CloneToCountries from '../components/CloneToCountries.vue'

const ProductAttributeService = new ProductAttributeProvider()
const ExportCurrencyRatesService = new ExportCurrencyRatesProvider()
const ExportCollectionsService = new ExportCollectionsProvider()

export default {
  components: {
    Draggable,
    CloneToCountries
  },
  data () {
    return {
      cloneKnownErrors: [
        {
          message: 'No active source collections to clone.',
          text: 'The source country has no active collections or clubs to clone. Choose a different source, or add them there first.'
        },
        {
          message: 'Duplicate collection names in source country.',
          text: 'The source country has two items with the same name. Rename the duplicates in the source country, then clone again.'
        }
      ],
      loading: {
        mainLoading: true,
        contentLoading: false
      },
      headers: [
        {
          text: '',
          value: 'handle',
          sortable: false,
          align: 'center',
          width: '40px'
        },
        {
          text: 'ชื่อ',
          value: 'name',
          sortable: false,
          align: 'center'
        },
        {
          text: 'ลิงค์',
          value: 'path',
          sortable: false,
          align: 'center'
        },
        {
          text: 'แท็ก',
          value: 'tags',
          sortable: false,
          align: 'center',
          width: '30%'
        },
        {
          text: 'ตัวเลือก',
          value: 'action',
          sortable: false,
          align: 'center'
        }
      ],
      memoItems: [],
      items: [],
      collections: [],
      clubs: [],
      brands: [],
      tags: [],
      shippingCountries: [],
      panels: [],
      query: {
        sortBy: 'priority',
        sortOrder: 'asc',
        country: null,
        brand: null
      },
      requiredRules: [
        (v) => (!!v && v.length > 0) || 'โปรดกรอก Field นี้'
      ],
      whiteSpaceRules: [
        (v) => !v?.toString()?.includes(' ') || 'ห้ามมีช่องว่างใน Field นี้'
      ]
    }
  },
  computed: {
    isDeveloper () {
      return getRole() === 'developer'
    },
    isEdit () {
      return JSON.stringify(this.mergedItems) !== JSON.stringify(this.memoItems)
    },
    mapBrand () {
      return this.query.brand !== null ? this.brands[this.query.brand] : null
    },
    nameRules () {
      return [
        ...this.requiredRules,
        (v) => {
          const duplicated = this.mergedItems.filter((item) => item.name.toLowerCase() === v.toLowerCase())

          return duplicated.length === 1 || 'ชื่อนี้ถูกใช้แล้ว'
        }
      ]
    },
    pathRules () {
      return [
        ...this.requiredRules,
        ...this.whiteSpaceRules,
        (v) => v.toLowerCase() === v || 'ต้องเป็นพิมพ์เล็กทั้งหมด',
        (v) => {
          const duplicated = this.mergedItems.filter((item) => item.path.toLowerCase() === v.toLowerCase())

          return duplicated.length === 1 || 'ลิงค์นี้ถูกใช้แล้ว'
        }
      ]
    },
    mergedItems () {
      return [
        ...this.collections.map((v) => ({
          ...v,
          group: 'collection'
        })),
        ...this.clubs.map((v) => ({
          ...v,
          group: 'club'
        }))
      ]
    }
  },
  watch: {
    'query.sortBy': {
      handler () {
        this.getItems(true)
      },
      deep: true
    },
    'query.sortOrder': {
      handler () {
        this.getItems(true)
      },
      deep: true
    },
    'query.country': {
      handler () {
        this.getItems(true)
      },
      deep: true
    },
    'query.brand': {
      handler () {
        this.getItems(true)
      },
      deep: true
    },
    'items': {
      handler (val) {
        this.collections = JSON.parse(JSON.stringify(val.filter((v) => v.group === 'collection')))
        this.clubs = JSON.parse(JSON.stringify(val.filter((v) => v.group === 'club')))
      },
      deep: true
    }
  },
  mounted () {
    this.initialize()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setModal: 'Components/setModal'
    }),
    cloneCollections (payload) {
      return ExportCollectionsService.cloneItems(payload)
    },
    onCloned (dest) {
      if (dest.includes(this.query.country)) {
        this.getItems(true)
      }
    },
    getShippingCountry (code) {
      return ShippingCountries.find((country) => country.code === code)?.country || code
    },
    onAddItem (group = 'collection') {
      if (group === 'collection') {
        this.collections.push({
          name: '',
          path: '',
          tags: [],
          group,
          country: this.query.country,
          brand: this.brands[this.query.brand],
          priority: 9999
        })
      } else if (group === 'club') {
        this.clubs.push({
          name: '',
          path: '',
          tags: [],
          group,
          country: this.query.country,
          brand: this.brands[this.query.brand],
          priority: 9999
        })
      }
    },
    async initialize () {
      try {
        this.loading.mainLoading = true

        await this.getBrands()
        await this.getTags()
        await this.getCountries()
      } catch (error) {
        console.error('initialize', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `[INITIALIZE-ERROR]: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading.mainLoading = false
      }
    },
    async getBrands () {
      try {
        const { data } = await ProductAttributeService.getProductAttribute('brands', {
          limit: 9999,
          sortBy: 'createdAt',
          sortOrder: 'asc'
        })

        this.brands = [...data.results.map((re) => re.name)]
        this.query.brand = this.brands.length ? 0 : null
      } catch (error) {
        console.error('getBrands', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `[GET-BRANDS-ERROR]: ${error.message}`,
          type: 'error'
        })
      }
    },
    async getTags () {
      try {
        const { data } = await ProductAttributeService.getProductAttribute('tags', {
          page: 1,
          itemsPerPage: 9999999,
          sortBy: ['id'],
          sortDesc: [true]
        })

        this.tags = data.results.map((r) => r.name)
      } catch (error) {
        console.error('getTags', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `[GET-TAGS-ERROR]: ${error.message}`,
          type: 'error'
        })
      }
    },
    async getCountries () {
      try {
        const { data } = await ExportCurrencyRatesService.getItems({})

        this.shippingCountries = data.map((r) => ({
          country: this.getShippingCountry(r.country),
          code: r.country
        }))
      } catch (error) {
        console.error('getCountries', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `[GET-COUNTRIES-ERROR]: ${error.message}`,
          type: 'error'
        })
      }
    },
    async getItems (isClear = false) {
      if (this.query.country === null || this.query.brand === null) {
        return
      }

      try {
        this.loading.contentLoading = true

        const { data } = await ExportCollectionsService.getItems({
          sortBy: this.query.sortBy,
          sortOrder: this.query.sortOrder,
          country: this.query.country,
          brand: this.brands[this.query.brand]
        })

        const mappedData = data.map((v) => ({
          ...v,
          name: v?.name?.id || v?.name
        }))

        if (isClear) {
          this.memoItems = []
          this.items = []
        }

        this.memoItems = JSON.parse(JSON.stringify(mappedData))
        this.items = mappedData
        this.panels = [0, 1]
      } catch (error) {
        console.error('getItems', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading.contentLoading = false
      }
    },
    confirmUpdateItem (item) {
      this.setModal({
        value: true,
        title: 'Update Item',
        message: `Do you want to update ${this.getShippingCountry(item.country)}?`,
        confirmText: 'Update',
        confirmType: 'success',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.updateItem(item.id)
      })
    },
    confirmUpdateAllItems () {
      this.setModal({
        value: true,
        title: 'Update Item',
        message: 'Do you want to update all items?',
        confirmText: 'Update',
        confirmType: 'success',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.updateAllItems()
      })
    },
    removeItem (group, index) {
      if (group === 'collection') {
        this.collections.splice(index, 1)
      } else if (group === 'club') {
        this.clubs.splice(index, 1)
      }
    },
    onReset () {
      this.items = JSON.parse(JSON.stringify(this.memoItems))
    },
    async updateAllItems () {
      try {
        this.loading.contentLoading = true

        const isValid = await this.$refs.form.validate()

        if (!isValid) {
          return
        }

        const toBeInActive = this.memoItems.filter((item) => !this.mergedItems.some((i) => i.id === item.id))
        const mappedItems = []

        for (const [index, item] of this.mergedItems.entries()) {
          mappedItems.push({
            ...item,
            active: true,
            priority: index + 1
          })
        }

        for (const item of toBeInActive) {
          mappedItems.push({
            ...item,
            active: false,
            priority: 9999
          })
        }

        await ExportCollectionsService.updateAllItems({
          items: mappedItems
        })

        this.getItems(true)
      } catch (error) {
        console.error('updateAllItems', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading.contentLoading = false
      }
    },
    async updateItem (id) {
      try {
        this.loading.contentLoading = true

        await ExportCollectionsService.updateItem(id, this.mergedItems.find((item) => item.id === id))

        this.getItems(true)
      } catch (error) {
        console.error('updateItem', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading.contentLoading = false
      }
    }
  }
}
</script>

<style scoped>
.custom-pannel::after {
  display: none !important;
}
.toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.toolbar span {
  font-weight: bold;
  font-size: 1.25rem;
}
.card-container {
  padding: 1.25rem 1.875rem;
  background-color: #fff;
  border-radius: 0.625rem;
  margin-top: 1.25rem;
}
.card-title {
  font-weight: bold;
  font-size: 1.35rem;
}
.warning-box {
  width: 100%;
  height: 224px;
  border: 2px dashed #BEBEBE;
  border-radius: 0.625rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #9c9c9c;
  gap: 0.3rem;
}
</style>

<style>
.search-collection-tag .v-select__selections input {
  max-height: 28px !important;
}
</style>
